import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type Lang = "zh" | "en";

const STORAGE_KEY = "cc_lang";

type Vars = Record<string, string | number>;

// Flat key → string. `{name}` placeholders are filled from t()'s vars argument.
// Exported so the i18n substrate can be characterization-tested (key parity, etc.)
// without rendering React — see i18n.test.ts.
export const translations: Record<Lang, Record<string, string>> = {
  zh: {
    "brand": "-/",
    "lang.zh": "中文",
    "lang.en": "EN",

    // Header
    "header.signOut": "退出登录",
    "header.agentSuffix": "智能体",
    "mode.cloud": "云端",
    "mode.local": "本地",

    // Sidebar / nav
    "nav.menu": "菜单",
    "nav.collapse": "收起",
    "nav.tasks": "历史任务",
    "workspace.chat": "对话",
    "workspace.researcher": "学术",
    "workspace.workspace": "工作区",
    "collab.computers": "电脑",
    "collab.computers.desc": "连接到这个工作区的云端与本地电脑。",
    "collab.online": "在线",
    "collab.offline": "离线",
    "collab.kind.cloud": "云端",
    "collab.kind.local": "本地",
    "collab.empty": "还没有电脑。连接一台开始吧。",
    "collab.connect.error": "生成连接命令失败,请稍后重试。",
    "collab.connect": "连接一台电脑",
    "collab.connect.desc": "在你自己的电脑上运行一条命令,它就会作为一台电脑加入这个工作区。",
    "collab.connect.generate": "生成连接命令",
    "collab.connect.macos": "macOS / Linux",
    "collab.connect.windows": "Windows(PowerShell)",
    "collab.connect.copy": "复制",
    "collab.connect.copied": "已复制",
    "collab.connect.tokenWarning": "这条命令几分钟内有效,拿到它的人都能把一台电脑接入你的工作区,请勿分享。",
    "collab.connect.waiting": "连接后,这台电脑会出现在上面的列表中。",
    "collab.runtimes.none": "未检测到 AI 工具",
    "collab.action.restart": "重启",
    "collab.action.upgrade": "升级",
    "collab.action.upgradeTo": "升级到 v{version}",
    "collab.action.upgrade.requesting": "正在准备升级…",
    "collab.action.upgrade.requestingDetail": "正在向电脑发送升级请求。",
    "collab.action.upgrade.installing": "正在升级到 v{version}",
    "collab.action.upgrade.installingDetail": "正在下载并安装更新，请保持电脑在线。",
    "collab.action.upgrade.reconnecting": "正在重新连接…",
    "collab.action.upgrade.reconnectingDetail": "新版本已安装，电脑服务正在重启。",
    "collab.action.upgrade.success": "已更新到 v{version}",
    "collab.action.upgrade.successDetail": "电脑已使用新版本重新上线。",
    "collab.action.upgrade.failed": "升级未完成",
    "collab.action.upgrade.failedDetail": "未能确认新版本，请检查电脑状态后重试。",
    "collab.action.delete": "删除",
    "collab.action.deleteConfirm": "确定要把这台电脑从工作区移除吗?",
    "collab.workspace.title": "工作区",
    "collab.workspace.desc": "这个工作区里的成员和电脑。",
    "collab.members": "成员",
    "collab.members.invite": "邀请成员",
    "collab.members.inviteHint": "把这个链接发给对方,对方登录后即可加入这个工作区。链接 7 天内有效、仅可用一次。",
    "collab.members.remove": "移除",
    "collab.members.error": "加载成员失败。",
    "collab.members.removeFailed": "移除成员失败。",
    "collab.role.owner": "所有者",
    "collab.role.member": "成员",
    "collab.invite.banner": "你被邀请加入一个工作区。",
    "collab.invite.join": "加入",
    "collab.invite.joined": "已加入工作区 ✓",
    "collab.invite.failed": "加入失败,邀请可能已失效或被使用。",
    "collab.switcher.label": "工作区:",
    "collab.workspace.untitled": "未命名工作区",
    "collab.channels": "频道",
    "collab.channels.desc": "按话题聊天的房间,人和智能体都能参与。",
    "collab.channels.new": "新建频道",
    "collab.channels.namePlaceholder": "频道名称",
    "collab.channels.topicPlaceholder": "主题(可选)",
    "collab.channels.private": "私密频道",
    "collab.channels.create": "创建",
    "collab.channels.cancel": "取消",
    "collab.channels.empty": "还没有频道,创建一个开始协作。",
    "collab.channels.selectPrompt": "选择一个频道开始聊天。",
    "collab.channels.noMessages": "还没有消息,来说点什么吧。",
    "collab.channels.composerPlaceholder": "发消息到 #{name}",
    "collab.channels.send": "发送",
    "collab.channels.loadEarlier": "加载更早的消息",
    "collab.channels.archive": "归档",
    "collab.channels.archived": "已归档",
    "collab.channels.you": "你",
    "collab.channels.agent": "智能体",
    "collab.channels.system": "系统",
    "collab.channels.error": "加载频道失败。",
    "collab.channels.sendError": "消息发送失败，请检查网络后重试。",
    "collab.tab.chat": "聊天",
    "collab.tab.tasks": "任务",
    "collab.tab.files": "文件",
    "collab.tab.soon": "即将推出",
    "collab.tasks.new": "新建任务",
    "collab.tasks.count": "{count} 个任务",
    "collab.tasks.titlePlaceholder": "任务标题",
    "collab.tasks.descriptionPlaceholder": "说明（可选）",
    "collab.tasks.owner": "负责人",
    "collab.tasks.unassigned": "未分配",
    "collab.tasks.agents": "智能体",
    "collab.tasks.emptyColumn": "暂无任务",
    "collab.tasks.status.todo": "待处理",
    "collab.tasks.status.in_progress": "进行中",
    "collab.tasks.status.in_review": "待审核",
    "collab.tasks.status.done": "已完成",
    "collab.tasks.start": "开始",
    "collab.tasks.submitReview": "提交审核",
    "collab.tasks.return": "退回",
    "collab.tasks.approve": "确认完成",
    "collab.tasks.reopen": "重新打开",
    "collab.tasks.openThread": "打开来源讨论",
    "collab.tasks.convert": "转为任务",
    "collab.tasks.linked": "已建任务",
    "collab.tasks.error": "任务操作失败。",
    "collab.computer.connected": "已连接",
    "collab.computer.info": "信息",
    "collab.computer.os": "系统",
    "collab.computer.version": "版本",
    "collab.computer.runtimes": "检测到的运行时",
    "collab.computer.updateAvailable": "有可用更新",
    "collab.computer.actions": "操作",
    "collab.computer.agents": "此电脑上的智能体",
    "collab.computer.agentsSoon": "智能体功能即将推出（Phase 3）。",
    "collab.computer.browserTitle": "浏览器控制",
    "collab.computer.browserDesc": "允许 Codex 和 Claude Code 控制独立浏览器；同一工作区会保留登录状态。",
    "collab.computer.browserEnabled": "已允许",
    "collab.computer.browserDisabled": "未允许",
    "collab.computer.browserUnavailable": "不可用，请升级 sj 并安装 Node.js 18+",
    "collab.computer.browserDisableConfirm": "关闭浏览器控制会停止此电脑上正在运行的任务。确定继续吗？",
    "collab.computer.browserSaving": "正在更新权限…",
    "collab.computer.browserError": "无法更新浏览器权限。",
    "collab.agent.new": "新建智能体",
    "collab.agent.cancel": "取消",
    "collab.agent.handle": "代号",
    "collab.agent.name": "显示名",
    "collab.agent.persona": "人设 / 系统提示（可选）",
    "collab.agent.create": "创建",
    "collab.agent.empty": "这台电脑上还没有智能体。",
    "collab.agent.hint": "在频道里用 @代号 提及它，它会在这台电脑上运行并回复。",
    "collab.agent.invalid": "代号需 2–30 位（字母、数字、_ 或 -）",
    "collab.agent.taken": "该 @代号 在此工作区已被占用",
    "collab.agent.runtimeUnavailable": "这台电脑当前没有可用的 AI 运行时。请安装 Codex CLI 或 Claude Code，然后重启电脑服务。",
    "collab.agent.cloudUnavailable": "此工作区尚未配置可用的神机云。请先连接本地电脑，或完成云端配置后重试。",
    "collab.agent.cloudFixed": "神机云内置智能体“灵”，可在频道中使用 @ling 提及。",
    "collab.agent.runtime": "运行时",
    "collab.agent.workspace": "工作目录",
    "collab.agent.workspaceHint": "该智能体运行所在的独立目录，默认与代号一致（须已存在，且在这台电脑连接时授权的允许范围内）。每个智能体一个目录 = 上下文按文件目录隔离。",
    "collab.agent.personaPlaceholder": "描述它的职责、语气与边界…（可选）",
    "collab.runtimes.notInstalled": "未安装",
    "collab.computer.workspaces": "智能体工作目录",
    "collab.computer.workspacesDesc": "每个智能体在各自的目录中运行，上下文按文件目录相互隔离。",
    "collab.computer.workspacesEmpty": "还没有智能体设置独立目录。",
    "collab.computer.serviceTitle": "电脑服务",
    "collab.computer.serviceDesc": "如果这台电脑看起来在线却没有响应，重启它。",
    "collab.computer.serviceUpgradeDesc": "有新版本 v{version}，升级会重启服务。",
    "collab.computer.verifyTitle": "在这台电脑的终端里验证",
    "collab.computer.verifyDesc": "如果这个页面和电脑的状态对不上，直接问电脑本身：",
    "collab.computer.deleteDesc": "永久移除这台电脑。需先删除其上的所有智能体。",
    "collab.action.upToDate": "已是最新",
    "collab.copy": "复制",
    "collab.computer.selectPrompt": "选择一台电脑查看详情。",
    "collab.status.running": "执行中",
    "collab.status.completed": "已完成",
    "collab.status.failed": "失败",
    "collab.thread.reply": "回复",
    "collab.thread.collapse": "收起回复",
    "collab.thread.replies": "{count} 条回复",
    "collab.thread.composerPlaceholder": "回复此消息…",
    "collab.working": "{names} 正在工作…",
    "collab.members.email": "邮箱",
    "collab.members.inviteNew": "重新生成链接",
    "collab.members.selectPrompt": "选择一位成员查看详情。",
    "collab.settings": "设置",
    "collab.chat.history": "历史会话",
    "collab.chat.new": "新对话",
    "collab.workspace.yours": "你的工作区",
    "collab.workspace.defaults": "默认",
    "collab.workspace.section": "工作区",
    "collab.workspace.new": "新建工作区",
    "collab.workspace.createTitle": "创建工作区",
    "collab.workspace.createDesc": "一个工作区就像一台服务器——成员、频道和电脑都在里面。",
    "collab.workspace.nameLabel": "工作区名称",
    "collab.workspace.namePlaceholder": "我的团队",
    "collab.workspace.slugLabel": "URL 短链",
    "collab.workspace.slugPlaceholder": "my-team",
    "collab.workspace.cancel": "取消",
    "collab.workspace.createBtn": "创建工作区",
    "collab.workspace.create": "创建",
    "collab.workspace.createError": "创建工作区失败。",

    // Admin
    "admin.title": "管理员",
    "admin.subtitle": "查看用户、余额、运行状态和后台错误，并可增加余额或发起错误修复。",
    "admin.refresh": "刷新",
    "admin.loadFailed": "无法加载管理员数据",
    "admin.users": "用户",
    "admin.accounts": "账号",
    "admin.agents": "智能体",
    "admin.ready": "就绪",
    "admin.sessions": "会话",
    "admin.running": "运行中",
    "admin.failed": "失败",
    "admin.quota": "余额",
    "admin.used": "已使用",
    "admin.paid": "已支付",
    "admin.search": "搜索用户、账号、Key 或容器",
    "admin.noUsers": "没有匹配用户",

    // Login
    "login.subtitle": "登录以连接你的云端智能体。",
    "login.email": "邮箱",
    "login.password": "密码",
    "login.emailCode": "验证码",
    "login.passwordMode": "密码登录",
    "login.codeMode": "验证码登录",
    "login.sendCode": "发送验证码",
    "login.codeSent": "如果账户存在，验证码已发送。",
    "login.codePlaceholder": "6 位验证码",
    "login.submit": "登录",
    "login.failed": "登录失败",
    "auth.invalidVerificationCode": "验证码错误，请重新输入。",
    "auth.sendVerificationCodeFailed": "验证码发送失败，请稍后重试。",
    "login.noAccount": "还没有账户？",
    "login.createOne": "立即注册",

    // Register
    "register.title": "创建账户",
    "register.subtitle": "注册以开通你的专属云端智能体。",
    "register.failed": "注册失败",
    "register.sectionAccount": "账户",
    "register.inviteCode": "邀请码",
    "register.inviteInvalid": "邀请码无效",
    "register.email": "邮箱",
    "register.password": "密码（至少 8 位）",
    "register.username": "用户名（可选）",
    "register.usernamePlaceholder": "至少 3 位，可稍后修改",
    "register.usernameInvalid": "用户名需 3–30 位（字母、数字、_ 或 -）",
    "register.usernameTaken": "该用户名已被占用",
    "register.emailCode": "邮箱验证码",
    "register.emailCodePlaceholder": "6 位验证码",
    "register.sendCode": "发送验证码",
    "register.codeSent": "验证码已发送，请检查邮箱。",
    "register.agentType": "智能体类型",
    "register.optCloud": "云端智能体（专属 Hermes 容器）",
    "register.optLocal": "本地智能体（即将推出）",
    "register.localNote": "本地智能体（配对桌面路由器）即将推出。你现在仍可创建账户。",
    "register.submit": "创建账户",
    "register.haveAccount": "已有账户？",
    "register.signIn": "登录",
    "register.keyAutoNote": "注册后我们会自动为你开通智能体密钥，无需手动填写。",

    // Billing
    "billing.open": "充值",
    "billing.title": "充值 / 升级",
    "billing.subtitle": "选择套餐，为你的智能体增加余额。",
    "billing.balance": "余额",
    "billing.currentQuota": "当前额度",
    "billing.used": "已使用",
    "billing.choosePlan": "选择套餐",
    "billing.addQuota": "增加 {quota} 余额",
    "billing.payNow": "立即支付 {price}",
    "billing.processing": "处理中……",
    "billing.success": "支付成功，余额已增加 ✓",
    "billing.failed": "支付失败",
    "billing.notConfigured": "支付功能尚未配置。",
    "billing.networkFailed": "无法连接支付服务，请检查网络后重试。",
    "billing.noBillingKey": "当前账户尚未开通计费密钥，请联系客服处理。",
    "billing.missingClientSecret": "支付初始化失败，请刷新页面后重试。",
    "billing.back": "返回套餐",
    "billing.loadFailed": "无法加载套餐",
    "billing.history.title": "余额变化记录",
    "billing.history.payment": "充值",
    "billing.history.admin": "管理员增加",
    "billing.history.after": "调整后 {quota}",
    "billing.history.balanceAfter": "余额 {balance}",
    "billing.history.empty": "暂无余额变化记录。",

    // Purchase
    "purchase.title": "开始体验自我进化的 AI - 灵",
    "purchase.subtitle": "验证邮箱后即可按实际使用量购买服务。新邮箱会先创建账户。",
    "purchase.verifyEmail": "验证邮箱",
    "purchase.sendCode": "发送验证码",
    "purchase.codeSent": "验证码已发送，请检查邮箱。",
    "purchase.continue": "继续",
    "purchase.setupPassword": "设置密码",
    "purchase.newAccountHint": "这个邮箱还没有账户。设置密码后即可继续购买。",
    "purchase.createAndContinue": "创建账户并继续",
    "purchase.choosePlan": "选择用量套餐",
    "purchase.priceList": "价格列表",
    "purchase.priceListHint": "先查看价格，验证邮箱后即可选择套餐并完成购买。",
    "purchase.planUsage": "增加 {quota} 可用量",

    // Agent screen
    "agent.localTitle": "本地智能体",
    "agent.localBody": "本地智能体（配对桌面路由器以在你自己的机器上运行）即将推出。",
    "agent.loadFailed": "无法加载智能体",
    "agent.provFailedTitle": "智能体开通失败",
    "agent.provFailedBody": "你的容器无法启动。请检查后端日志后重试。",
    "agent.recheck": "重新检查",
    "agent.provisioningTitle": "正在开通你的专属智能体……",
    "agent.provisioningBody": "状态：{status}。正在构建并启动你的 Hermes 容器——首次运行可能需要几分钟。",
    "agent.settingUp": "正在设置你的智能体……",
    "agent.readyHint": "你的智能体已就绪。发送消息开始对话。",
    "agent.loadEarlier": "加载更早的消息",
    "agent.roleYou": "你",
    "agent.roleAgent": "灵",
    "agent.composerPlaceholder": "对话（Shift+Enter 发送）",
    "agent.send": "发送",
    "agent.voice": "语音输入",
    "agent.error": "智能体暂时出错了，请稍后重试。如果问题持续存在，请联系客服。",
    "agent.quotaExceeded": "你的使用额度已用完。请前往「账单」充值后再继续使用。",
    "agent.quotaLow": "你的剩余额度不足以启动这个任务。长任务需要至少保留 ${minimum} 额度，请前往「账单」充值后再继续使用。",
    "agent.rateLimited": "当前请求过于频繁，已达到接口速率上限。请稍等片刻后重试；若持续出现，可能是当前额度已接近上限，请检查你的账户额度。",
    "agent.contextTooLarge": "这段对话太长了。请新建对话，或缩短消息后重试。",
    "agent.credentialRejected": "模型服务凭证暂时不可用。请稍后重试；如果问题持续存在，请联系客服。",
    "agent.timeout": "这个任务运行时间过长，已自动停止。请把任务拆成更小步骤，或先让智能体提取 PDF 重点后再做估值分析。",
    "agent.interrupted": "这次任务在完成前被中断了，通常是服务重启或连接中断导致。请重新发送；如果是长任务，建议拆成更小步骤。",
    "agent.missingDeliverable": "文件未成功生成，请重试。",
    "agent.missingDeliverableProcessOnly": "你要的文件没有生成成功，只产出了过程文件（{files}）。可以在下方的“过程文件”里查看，或重新试一次。",
    "agent.missingFormats": "{formats} 没有生成，可以让我重试。",
    "agent.deliverablePending": "暂未完成 {formats} 生成。",
    "local.devicesTitle": "本地设备",
    "local.refresh": "刷新",
    "local.pair": "配对设备",
    "local.pairCode": "配对码",
    "local.noDevices": "暂无已配对的设备。点击“配对设备”获取配对码，并在桌面应用中输入。",
    "local.online": "在线",
    "local.offline": "离线",
    "local.readyHint": "已选择设备。发送一条消息即可开始。",
    "local.pickDevice": "请选择一台在线设备以开始。",
    "local.deviceOffline": "该设备已离线。",
    "local.composerPlaceholder": "对话（Shift+Enter 发送）",
    "local.approve": "批准",
    "local.reject": "拒绝",
    "local.approvalTitle": "需要批准",
    "local.tasksTitle": "最近的本地任务",
    "local.tasksHint": "该设备最近两周更新过的任务。",
    "local.noTasks": "最近没有任务。",
    "local.tasksOffline": "选择一台在线设备以查看其任务。",
    "local.continuing": "继续：",
    "local.notResumable": "该任务无法继续，仅可查看记录。",
    "local.historyFailed": "无法加载任务记录。",
    "local.noHistory": "该任务暂无可显示的对话记录。",
    "local.pairHint": "在桌面应用（设置 → 账户与本地路由）中输入此配对码。",
    "local.removeDevice": "删除该设备",
    "agent.attach": "添加文件",
    "agent.removeAttachment": "移除",
    "agent.tooLarge": "附件过大（单个文件上限 100MB）",
    "agent.thinking": "灵正在处理，请稍候……",
    "agent.backgroundHint": "这个任务可能需要更久。灵会在后台继续工作，你可以稍后回到历史对话查看结果。",
    "agent.backgroundHintSubmitting": "正在上传附件并提交任务。提交成功后，灵会在后台继续工作，你可以稍后回到历史对话查看结果。",
    "agent.progressLabel": "灵的处理进度",
    "agent.progress.working": "灵仍在处理请求。",
    "agent.progress.started": "已连接灵，开始处理请求。",
    "agent.progress.quotaChecked": "额度和运行环境检查完成。",
    "agent.progress.workspaceReady": "工作区状态已准备好。",
    "agent.progress.running": "灵正在分析任务。",
    "agent.progress.background": "灵会在后台继续运行，你可以离开，稍后回来查看结果。",
    "agent.progress.routing": "正在规划合适的执行方式。",
    "agent.progress.stillWorking": "灵仍在工作，已用时 {elapsed}。",
    "agent.progress.longTask": "长文档或检索任务可能需要几分钟。灵会在后台继续工作，你可以稍后回到历史对话查看结果。",
    "agent.progress.connected": "连接正常，正在等待灵返回结果。",
    "agent.progress.retryingCredential": "正在刷新灵的凭据并重试。",
    "agent.progress.refreshingCredential": "正在后台刷新灵的凭据。",
    "agent.progress.credentialRefreshed": "灵的凭据已刷新。",
    "agent.progress.runtimeRecovering": "运行环境超时，正在恢复。",
    "agent.progress.runtimeRecovered": "运行环境已恢复。",
    "agent.progress.timedOut": "任务已超过本次运行时间上限，正在停止。",
    "agent.progress.decomposing": "正在自动规划并拆解为较小步骤。",
    "agent.progress.subtaskRunning": "正在执行拆解后的步骤：{elapsed}。",
    "agent.progress.synthesizing": "正在汇总分步结果。",
    "agent.progress.durableRun": "灵已进入持续运行模式：后端会实时同步进度，不再拆成限时小步骤。",
    "agent.progress.reasoningAvailable": "灵已生成推理上下文。",
    "agent.plan.label": "执行计划",
    "agent.plan.title": "执行计划",
    "agent.steps.item": "步骤 {n}/{total}：{title}",
    "agent.generatedFiles": "智能体生成的文件",
    "agent.filesTitle": "文件",
    "agent.processFiles": "过程文件（{count}）",
    "agent.processFilesHint": "智能体工作时用到的脚本、日志等，一般不需要打开",
    "agent.referenceFiles": "参考资料（{count}）",
    "agent.referenceFilesHint": "智能体查资料时下载的网页、文本等，可用来核对出处",
    "agent.downloadFile": "下载 {name}",
    "agent.preview.loading": "正在生成预览……",
    "agent.preview.tooLarge": "文件较大，预览已跳过，请下载查看。",
    "agent.preview.unavailable": "无法预览该文件，请下载查看。",
    "agent.preview.show": "显示预览",
    "agent.preview.open": "预览 {name}",
    "agent.preview.download": "下载",
    "agent.preview.outline": "目录",
    "agent.preview.remoteImageBlocked": "远程图片已阻止，可下载文件后查看",
    "agent.preview.unsupported": "暂不支持预览此文件，可以下载后查看。",
    "agent.preview.htmlSafety": "安全预览：脚本、表单和外部网络请求已禁用。",
    "agent.preview.htmlLive": "隔离预览：页面在独立环境中运行，图表和样式正常显示，但读不到你的账号信息，也不能向外发送数据。",
    "agent.preview.loadVideo": "点击加载并播放视频",
    "agent.preview.loadingVideo": "正在加载视频……",
    "agent.preview.videoTooLarge": "视频较大，请下载后播放。",
    "agent.preview.kind.markdown": "Markdown 预览",
    "agent.preview.kind.html": "HTML 安全预览",
    "agent.preview.kind.image": "图片预览",
    "agent.preview.kind.video": "视频",
    "agent.preview.kind.pdf": "PDF 预览",
    "agent.preview.pdfPage": "第 {page} / {total} 页",
    "agent.preview.pdfPrev": "上一页",
    "agent.preview.pdfNext": "下一页",
    "agent.preview.imageTooLarge": "图片 {name} 较大，请下载查看",
    "agent.preview.imageUnavailable": "图片 {name} 无法显示，请下载查看",
    "agent.preview.kind.unsupported": "文件",
    "history.new": "新建对话",
    "history.title": "历史对话",
    "history.empty": "暂无历史记录",
    "common.close": "关闭",
    "common.retry": "重试",
    "settings.title": "设置",
    "settings.language": "语言",
    "settings.save": "保存",
    "settings.group.personal": "个人",
    "settings.group.workspace": "工作区",
    "settings.section.account": "账户",
    "settings.session": "登录状态",
    "settings.signout": "退出登录",
    "settings.username": "用户名",
    "settings.username.placeholder": "用户名",
    "settings.username.hint": "全局唯一，至少 3 位（字母、数字、_ 或 -）。可留空。",
    "settings.username.invalid": "用户名需 3–30 位（字母、数字、_ 或 -）",
    "settings.username.taken": "该用户名已被占用",
    "settings.username.saved": "已保存 ✓",
    "settings.admin.desc": "查看用户、余额、运行状态和后台错误。",
    "settings.admin.open": "打开管理后台",
    "cron.title": "定时任务",
    "cron.empty": "暂无定时任务。在下方创建一个，智能体会按计划自动执行并把结果存入历史对话。",
    "cron.createTitle": "新建定时任务",
    "cron.name": "名称",
    "cron.prompt": "提示词（按计划发送给智能体）",
    "cron.schedule": "执行计划",
    "cron.kindDaily": "每天定时",
    "cron.kindInterval": "按间隔（分钟）",
    "cron.every": "每",
    "cron.minutes": "分钟",
    "cron.dailyAt": "每天",
    "cron.next": "下次",
    "cron.paused": "已暂停",
    "cron.pause": "暂停",
    "cron.resume": "恢复",
    "cron.delete": "删除",
    "cron.create": "创建",
    "session.noOutput": "（无输出）",
    "agent.attachmentLabel": "附件",

    // Agent status values
    "status.provisioning": "开通中",
    "status.ready": "就绪",
    "status.failed": "失败",
    "status.suspended": "已暂停",
    "status.queued": "排队中",
    "status.running": "进行中",
    "status.completed": "已完成",
    "status.cancelled": "已取消",

    // Generic
    "common.failed": "操作失败"
  },
  en: {
    "brand": "-/",
    "lang.zh": "中文",
    "lang.en": "EN",

    "header.signOut": "Sign out",
    "header.agentSuffix": "agent",
    "mode.cloud": "Cloud",
    "mode.local": "Local",

    "nav.menu": "Menu",
    "nav.collapse": "Collapse",
    "nav.tasks": "Tasks",
    "workspace.chat": "Chat",
    "workspace.researcher": "Academic",
    "workspace.workspace": "Workspace",
    "collab.computers": "Computers",
    "collab.computers.desc": "Cloud and local computers connected to this workspace.",
    "collab.online": "Online",
    "collab.offline": "Offline",
    "collab.kind.cloud": "Cloud",
    "collab.kind.local": "Local",
    "collab.empty": "No computers yet. Connect one to get started.",
    "collab.connect.error": "Couldn't generate the connect command. Please try again.",
    "collab.connect": "Connect a computer",
    "collab.connect.desc": "Run one command on your own machine and it joins this workspace as a computer.",
    "collab.connect.generate": "Generate connect command",
    "collab.connect.macos": "macOS / Linux",
    "collab.connect.windows": "Windows (PowerShell)",
    "collab.connect.copy": "Copy",
    "collab.connect.copied": "Copied",
    "collab.connect.tokenWarning": "This command is valid for a few minutes — anyone with it can attach a computer to your workspace, so don't share it.",
    "collab.connect.waiting": "Once connected, this computer appears in the list above.",
    "collab.runtimes.none": "No AI tools detected",
    "collab.action.restart": "Restart",
    "collab.action.upgrade": "Upgrade",
    "collab.action.upgradeTo": "Upgrade (v{version})",
    "collab.action.upgrade.requesting": "Preparing upgrade…",
    "collab.action.upgrade.requestingDetail": "Sending the upgrade request to this computer.",
    "collab.action.upgrade.installing": "Upgrading to v{version}",
    "collab.action.upgrade.installingDetail": "Downloading and installing the update. Keep the computer online.",
    "collab.action.upgrade.reconnecting": "Reconnecting…",
    "collab.action.upgrade.reconnectingDetail": "The update is installed and the computer service is restarting.",
    "collab.action.upgrade.success": "Updated to v{version}",
    "collab.action.upgrade.successDetail": "The computer is back online on the new version.",
    "collab.action.upgrade.failed": "Upgrade not completed",
    "collab.action.upgrade.failedDetail": "The new version was not confirmed. Check the computer and try again.",
    "collab.action.delete": "Delete",
    "collab.action.deleteConfirm": "Remove this computer from the workspace?",
    "collab.workspace.title": "Workspace",
    "collab.workspace.desc": "The people and computers in this workspace.",
    "collab.members": "Members",
    "collab.members.invite": "Invite member",
    "collab.members.inviteHint": "Send this link to someone; after they sign in they join this workspace. Valid for 7 days, single use.",
    "collab.members.remove": "Remove",
    "collab.members.error": "Failed to load members.",
    "collab.members.removeFailed": "Failed to remove member.",
    "collab.role.owner": "Owner",
    "collab.role.member": "Member",
    "collab.invite.banner": "You've been invited to a workspace.",
    "collab.invite.join": "Join",
    "collab.invite.joined": "Joined the workspace ✓",
    "collab.invite.failed": "Couldn't join — the invite may be invalid or already used.",
    "collab.switcher.label": "Workspace:",
    "collab.workspace.untitled": "Untitled workspace",
    "collab.channels": "Channels",
    "collab.channels.desc": "Topic-based rooms where people and agents talk.",
    "collab.channels.new": "New channel",
    "collab.channels.namePlaceholder": "Channel name",
    "collab.channels.topicPlaceholder": "Topic (optional)",
    "collab.channels.private": "Private channel",
    "collab.channels.create": "Create",
    "collab.channels.cancel": "Cancel",
    "collab.channels.empty": "No channels yet — create one to start collaborating.",
    "collab.channels.selectPrompt": "Pick a channel to start chatting.",
    "collab.channels.noMessages": "No messages yet. Say hi.",
    "collab.channels.composerPlaceholder": "Message #{name}",
    "collab.channels.send": "Send",
    "collab.channels.loadEarlier": "Load earlier messages",
    "collab.channels.archive": "Archive",
    "collab.channels.archived": "Archived",
    "collab.channels.you": "You",
    "collab.channels.agent": "Agent",
    "collab.channels.system": "System",
    "collab.channels.error": "Couldn't load channels.",
    "collab.channels.sendError": "Couldn't send the message. Check your connection and try again.",
    "collab.tab.chat": "Chat",
    "collab.tab.tasks": "Tasks",
    "collab.tab.files": "Files",
    "collab.tab.soon": "Coming soon",
    "collab.tasks.new": "New task",
    "collab.tasks.count": "{count} tasks",
    "collab.tasks.titlePlaceholder": "Task title",
    "collab.tasks.descriptionPlaceholder": "Description (optional)",
    "collab.tasks.owner": "Owner",
    "collab.tasks.unassigned": "Unassigned",
    "collab.tasks.agents": "Agents",
    "collab.tasks.emptyColumn": "No tasks",
    "collab.tasks.status.todo": "To do",
    "collab.tasks.status.in_progress": "In progress",
    "collab.tasks.status.in_review": "In review",
    "collab.tasks.status.done": "Done",
    "collab.tasks.start": "Start",
    "collab.tasks.submitReview": "Submit for review",
    "collab.tasks.return": "Return",
    "collab.tasks.approve": "Approve",
    "collab.tasks.reopen": "Reopen",
    "collab.tasks.openThread": "Open source discussion",
    "collab.tasks.convert": "Create task",
    "collab.tasks.linked": "Task linked",
    "collab.tasks.error": "Task action failed.",
    "collab.computer.connected": "Connected",
    "collab.computer.info": "Info",
    "collab.computer.os": "OS",
    "collab.computer.version": "Version",
    "collab.computer.runtimes": "Detected runtimes",
    "collab.computer.updateAvailable": "update available",
    "collab.computer.actions": "Actions",
    "collab.computer.agents": "Agents on this computer",
    "collab.computer.agentsSoon": "Agents are coming in Phase 3.",
    "collab.computer.browserTitle": "Browser control",
    "collab.computer.browserDesc": "Allow Codex and Claude Code to control an isolated browser with logins retained per workspace.",
    "collab.computer.browserEnabled": "Allowed",
    "collab.computer.browserDisabled": "Not allowed",
    "collab.computer.browserUnavailable": "Unavailable; update sj and install Node.js 18+",
    "collab.computer.browserDisableConfirm": "Turning off browser control stops running tasks on this computer. Continue?",
    "collab.computer.browserSaving": "Updating permission…",
    "collab.computer.browserError": "Could not update browser permission.",
    "collab.agent.new": "New agent",
    "collab.agent.cancel": "Cancel",
    "collab.agent.handle": "Codename",
    "collab.agent.name": "Display name",
    "collab.agent.persona": "Persona / system prompt (optional)",
    "collab.agent.create": "Create",
    "collab.agent.empty": "No agents on this computer yet.",
    "collab.agent.hint": "@mention the codename in a channel — it runs on this computer and replies.",
    "collab.agent.invalid": "Codename must be 2–30 chars (letters, digits, _ or -)",
    "collab.agent.taken": "That @codename is taken in this workspace",
    "collab.agent.runtimeUnavailable": "This computer has no available AI runtime. Install Codex CLI or Claude Code, then restart the computer service.",
    "collab.agent.cloudUnavailable": "This workspace has no available Shenji Cloud runtime. Connect a local computer or finish cloud setup first.",
    "collab.agent.cloudFixed": "Shenji Cloud includes Ling. Mention @ling in a channel to use it.",
    "collab.agent.runtime": "Runtime",
    "collab.agent.workspace": "Workspace directory",
    "collab.agent.workspaceHint": "A dedicated directory this agent runs in, matching its codename by default (must already exist and be inside the allowlist authorized when this computer connected). One directory per agent = context isolated at the file-directory level.",
    "collab.agent.personaPlaceholder": "Describe its role, tone and boundaries… (optional)",
    "collab.runtimes.notInstalled": "not installed",
    "collab.computer.workspaces": "Agent workspaces",
    "collab.computer.workspacesDesc": "Each agent runs in its own directory, so their context is isolated at the file-directory level.",
    "collab.computer.workspacesEmpty": "No agent has a dedicated directory yet.",
    "collab.computer.serviceTitle": "Computer service",
    "collab.computer.serviceDesc": "If this computer looks online but stops responding, restart it.",
    "collab.computer.serviceUpgradeDesc": "Version v{version} is available — upgrading restarts the service.",
    "collab.computer.verifyTitle": "Verify from this computer's terminal",
    "collab.computer.verifyDesc": "If this page and the computer disagree, ask the computer itself:",
    "collab.computer.deleteDesc": "Permanently remove this computer. Delete all its agents first.",
    "collab.action.upToDate": "Up to date",
    "collab.copy": "Copy",
    "collab.computer.selectPrompt": "Select a computer to see details.",
    "collab.status.running": "Running",
    "collab.status.completed": "Done",
    "collab.status.failed": "Failed",
    "collab.thread.reply": "Reply",
    "collab.thread.collapse": "Hide replies",
    "collab.thread.replies": "{count} replies",
    "collab.thread.composerPlaceholder": "Reply to this message…",
    "collab.working": "{names} is working…",
    "collab.members.email": "Email",
    "collab.members.inviteNew": "Regenerate link",
    "collab.members.selectPrompt": "Select a member to see details.",
    "collab.settings": "Settings",
    "collab.chat.history": "Conversations",
    "collab.chat.new": "New chat",
    "collab.workspace.yours": "Your workspaces",
    "collab.workspace.defaults": "Default",
    "collab.workspace.section": "Workspaces",
    "collab.workspace.new": "New workspace",
    "collab.workspace.createTitle": "Create workspace",
    "collab.workspace.createDesc": "A workspace is like a server — its members, channels, and computers live inside.",
    "collab.workspace.nameLabel": "Workspace name",
    "collab.workspace.namePlaceholder": "My Team",
    "collab.workspace.slugLabel": "URL slug",
    "collab.workspace.slugPlaceholder": "my-team",
    "collab.workspace.cancel": "Cancel",
    "collab.workspace.createBtn": "Create workspace",
    "collab.workspace.create": "Create",
    "collab.workspace.createError": "Couldn't create workspace.",

    "admin.title": "Admin",
    "admin.subtitle": "View users, balance, runtime state, and backend errors, and add balance or request fixes.",
    "admin.refresh": "Refresh",
    "admin.loadFailed": "Could not load admin data",
    "admin.users": "Users",
    "admin.accounts": "accounts",
    "admin.agents": "Agents",
    "admin.ready": "ready",
    "admin.sessions": "Sessions",
    "admin.running": "running",
    "admin.failed": "failed",
    "admin.quota": "Balance",
    "admin.used": "used",
    "admin.paid": "Paid",
    "admin.search": "Search users, accounts, keys, or containers",
    "admin.noUsers": "No matching users",

    "login.subtitle": "Sign in to reach your cloud agent.",
    "login.email": "Email",
    "login.password": "Password",
    "login.emailCode": "Verification code",
    "login.passwordMode": "Password",
    "login.codeMode": "Email code",
    "login.sendCode": "Send code",
    "login.codeSent": "If the account exists, a code has been sent.",
    "login.codePlaceholder": "6-digit code",
    "login.submit": "Sign in",
    "login.failed": "Sign in failed",
    "auth.invalidVerificationCode": "Invalid verification code. Please try again.",
    "auth.sendVerificationCodeFailed": "Could not send verification code. Please try again later.",
    "login.noAccount": "No account?",
    "login.createOne": "Create one",

    "register.title": "Create your account",
    "register.subtitle": "Register to provision your private cloud agent.",
    "register.failed": "Registration failed",
    "register.sectionAccount": "Account",
    "register.inviteCode": "Invitation code",
    "register.inviteInvalid": "Invalid invitation code",
    "register.email": "Email",
    "register.password": "Password (min 8 chars)",
    "register.username": "Username (optional)",
    "register.usernamePlaceholder": "At least 3 characters, editable later",
    "register.usernameInvalid": "Username must be 3–30 chars (letters, digits, _ or -)",
    "register.usernameTaken": "That username is taken",
    "register.emailCode": "Email verification code",
    "register.emailCodePlaceholder": "6-digit code",
    "register.sendCode": "Send code",
    "register.codeSent": "Verification code sent. Check your inbox.",
    "register.agentType": "Agent type",
    "register.optCloud": "Cloud agent (private Hermes container)",
    "register.optLocal": "Local agent (coming soon)",
    "register.localNote": "Local agents (pairing a desktop router) are coming soon. You can still create the account now.",
    "register.submit": "Create account",
    "register.haveAccount": "Already have an account?",
    "register.signIn": "Sign in",
    "register.keyAutoNote": "We provision your agent's API key automatically after sign-up — no key to paste.",

    "billing.open": "Top up",
    "billing.title": "Top up / Upgrade",
    "billing.subtitle": "Pick a plan to add balance to your agent.",
    "billing.balance": "Balance",
    "billing.currentQuota": "Current quota",
    "billing.used": "Used",
    "billing.choosePlan": "Choose a plan",
    "billing.addQuota": "Adds {quota} balance",
    "billing.payNow": "Pay {price}",
    "billing.processing": "Processing…",
    "billing.success": "Payment succeeded — balance added ✓",
    "billing.failed": "Payment failed",
    "billing.notConfigured": "Payments are not configured.",
    "billing.networkFailed": "Could not reach the payment service. Check your connection and try again.",
    "billing.noBillingKey": "This account does not have a billing key yet. Contact support to fix it.",
    "billing.missingClientSecret": "Payment setup failed. Refresh the page and try again.",
    "billing.back": "Back to plans",
    "billing.loadFailed": "Could not load plans",
    "billing.history.title": "Balance history",
    "billing.history.payment": "Top-up",
    "billing.history.admin": "Admin top-up",
    "billing.history.after": "After {quota}",
    "billing.history.balanceAfter": "Balance {balance}",
    "billing.history.empty": "No balance changes yet.",

    "purchase.title": "Experience self-evolving AI - Ling",
    "purchase.subtitle": "Verify your email, then purchase service by actual usage. New emails create an account first.",
    "purchase.verifyEmail": "Verify email",
    "purchase.sendCode": "Send code",
    "purchase.codeSent": "Verification code sent. Check your inbox.",
    "purchase.continue": "Continue",
    "purchase.setupPassword": "Set password",
    "purchase.newAccountHint": "This email does not have an account yet. Set a password to continue.",
    "purchase.createAndContinue": "Create account and continue",
    "purchase.choosePlan": "Choose usage package",
    "purchase.priceList": "Price list",
    "purchase.priceListHint": "Review pricing first, then verify your email to choose a package and purchase.",
    "purchase.planUsage": "Adds {quota} usage",

    "agent.localTitle": "Local agent",
    "agent.localBody": "Local agents (pairing a desktop router to run agents on your own machine) are coming soon.",
    "agent.loadFailed": "Could not load agent",
    "agent.provFailedTitle": "Agent provisioning failed",
    "agent.provFailedBody": "Your container could not be started. Check the backend logs and try again.",
    "agent.recheck": "Re-check",
    "agent.provisioningTitle": "Provisioning your private agent…",
    "agent.provisioningBody": "Status: {status}. Building and starting your Hermes container — this can take a few minutes on first run.",
    "agent.settingUp": "Setting up your agent…",
    "agent.readyHint": "Your agent is ready. Send a message to get started.",
    "agent.loadEarlier": "Load earlier messages",
    "agent.roleYou": "You",
    "agent.roleAgent": "Agent",
    "agent.composerPlaceholder": "Chat (Shift+Enter to send)",
    "agent.send": "Send",
    "agent.voice": "Voice input",
    "agent.error": "The agent ran into a temporary error. Please try again shortly, and contact support if it keeps happening.",
    "agent.quotaExceeded": "You've used up your usage quota. Please top up under Billing to continue.",
    "agent.quotaLow": "Your remaining quota is too low to start this task. Long tasks need at least ${minimum} in reserve. Please top up under Billing to continue.",
    "agent.rateLimited": "Too many requests right now — the rate limit was hit. Please wait a moment and try again; if it keeps happening, your quota may be nearly used up, so check your account balance.",
    "agent.contextTooLarge": "This conversation is too long. Start a new chat or shorten your message, then try again.",
    "agent.credentialRejected": "The model service credential is temporarily unavailable. Please try again shortly, and contact support if it keeps happening.",
    "agent.timeout": "This task ran too long and was stopped. Split it into smaller steps, or ask the agent to extract the PDF highlights first before doing the valuation analysis.",
    "agent.interrupted": "This task was interrupted before a result was delivered, usually because the service restarted or the connection dropped. Please resend it; for long tasks, split it into smaller steps.",
    "agent.missingDeliverable": "The file was not created successfully. Please try again.",
    "agent.missingDeliverableProcessOnly": "The file you asked for was not created — only helper files were produced ({files}). You can find them under “Process files” below, or try again.",
    "agent.missingFormats": "The {formats} was not created. You can ask me to try again.",
    "agent.deliverablePending": "The {formats} has not been created yet.",
    "local.devicesTitle": "Local devices",
    "local.refresh": "Refresh",
    "local.pair": "Pair a device",
    "local.pairCode": "Pairing code",
    "local.noDevices": "No paired devices yet. Click “Pair a device” to get a code, then enter it in the desktop app.",
    "local.online": "online",
    "local.offline": "offline",
    "local.readyHint": "Device selected. Send a message to get started.",
    "local.pickDevice": "Pick an online device to get started.",
    "local.deviceOffline": "That device is offline.",
    "local.composerPlaceholder": "Chat (Shift+Enter to send)",
    "local.approve": "Approve",
    "local.reject": "Reject",
    "local.approvalTitle": "Approval required",
    "local.tasksTitle": "Recent local tasks",
    "local.tasksHint": "Tasks updated on this device in the last two weeks.",
    "local.noTasks": "No recent tasks.",
    "local.tasksOffline": "Pick an online device to see its tasks.",
    "local.continuing": "Continuing: ",
    "local.notResumable": "This task can't be continued — view only.",
    "local.historyFailed": "Couldn't load the task's history.",
    "local.noHistory": "No conversation history to show for this task.",
    "local.pairHint": "Enter this code in the desktop app (Settings → Account & Local Router).",
    "local.removeDevice": "Remove this device",
    "agent.attach": "Attach files",
    "agent.removeAttachment": "Remove",
    "agent.tooLarge": "Attachment too large (100MB per file max)",
    "agent.thinking": "Agent is working, this can take a while…",
    "agent.backgroundHint": "This task may take longer. The agent will keep working in the background, and you can come back to the conversation history later.",
    "agent.backgroundHintSubmitting": "Uploading attachments and submitting the task. Once submitted, the agent will keep working in the background and you can check the conversation history later.",
    "agent.progressLabel": "Agent progress",
    "agent.progress.working": "The agent is still working.",
    "agent.progress.started": "Connected to the cloud agent.",
    "agent.progress.quotaChecked": "Usage quota and runtime checks passed.",
    "agent.progress.workspaceReady": "Workspace state is ready.",
    "agent.progress.running": "The agent is processing the request.",
    "agent.progress.background": "The agent keeps running in the background — you can leave and come back later to check the result.",
    "agent.progress.routing": "Planning the best execution workflow.",
    "agent.progress.stillWorking": "The agent is still working. Elapsed: {elapsed}.",
    "agent.progress.longTask": "Large document or research tasks can take a few minutes. The agent will keep working in the background, and you can check the conversation history later.",
    "agent.progress.connected": "Connection is healthy; waiting for the agent result.",
    "agent.progress.retryingCredential": "Refreshing the agent credential and retrying.",
    "agent.progress.refreshingCredential": "Refreshing the agent credential in the background.",
    "agent.progress.credentialRefreshed": "Agent credential refreshed.",
    "agent.progress.runtimeRecovering": "The runtime timed out and is recovering.",
    "agent.progress.runtimeRecovered": "Agent runtime recovered.",
    "agent.progress.timedOut": "The task exceeded this run's time limit and is stopping.",
    "agent.progress.decomposing": "Planning and splitting the work into smaller steps.",
    "agent.progress.subtaskRunning": "Running a decomposed step. Elapsed: {elapsed}.",
    "agent.progress.synthesizing": "Combining the step outputs into the final answer.",
    "agent.progress.durableRun": "Using a durable Ling run: the backend will stream progress without splitting this request into timed sub-steps.",
    "agent.progress.reasoningAvailable": "Ling produced reasoning context.",
    "agent.plan.label": "Execution plan",
    "agent.plan.title": "Plan",
    "agent.steps.item": "Step {n}/{total}: {title}",
    "agent.generatedFiles": "Files generated by the agent",
    "agent.filesTitle": "Files",
    "agent.processFiles": "Process files ({count})",
    "agent.processFilesHint": "Scripts, logs and other files the agent used while working; usually not needed",
    "agent.referenceFiles": "Reference material ({count})",
    "agent.referenceFilesHint": "Pages and text the agent downloaded while researching, for checking sources",
    "agent.downloadFile": "Download {name}",
    "agent.preview.loading": "Loading preview…",
    "agent.preview.tooLarge": "This file is large; preview was skipped. Download it to view.",
    "agent.preview.unavailable": "Preview unavailable. Download the file to view it.",
    "agent.preview.show": "Show preview",
    "agent.preview.open": "Preview {name}",
    "agent.preview.download": "Download",
    "agent.preview.outline": "Outline",
    "agent.preview.remoteImageBlocked": "Remote image blocked; download the file to view it",
    "agent.preview.unsupported": "Preview is not available for this file. Download it to view.",
    "agent.preview.htmlSafety": "Safe preview: scripts, forms, and external network requests are disabled.",
    "agent.preview.htmlLive": "Isolated preview: the page runs in a separate sandbox so charts and styles work, but it cannot read your account or send data out.",
    "agent.preview.loadVideo": "Load and play video",
    "agent.preview.loadingVideo": "Loading video…",
    "agent.preview.videoTooLarge": "This video is large. Download it to play.",
    "agent.preview.kind.markdown": "Markdown preview",
    "agent.preview.kind.html": "Safe HTML preview",
    "agent.preview.kind.image": "Image preview",
    "agent.preview.kind.video": "Video",
    "agent.preview.kind.pdf": "PDF preview",
    "agent.preview.pdfPage": "Page {page} of {total}",
    "agent.preview.pdfPrev": "Previous",
    "agent.preview.pdfNext": "Next",
    "agent.preview.imageTooLarge": "Image {name} is too large to preview. Download to view.",
    "agent.preview.imageUnavailable": "Image {name} could not be shown. Download to view.",
    "agent.preview.kind.unsupported": "File",
    "history.new": "New chat",
    "history.title": "History",
    "history.empty": "No history yet",
    "common.close": "Close",
    "common.retry": "Retry",
    "settings.title": "Settings",
    "settings.language": "Language",
    "settings.save": "Save",
    "settings.group.personal": "Personal",
    "settings.group.workspace": "Workspace",
    "settings.section.account": "Account",
    "settings.session": "Session",
    "settings.signout": "Log out",
    "settings.username": "Username",
    "settings.username.placeholder": "username",
    "settings.username.hint": "Globally unique, at least 3 chars (letters, digits, _ or -). Optional.",
    "settings.username.invalid": "Username must be 3–30 chars (letters, digits, _ or -)",
    "settings.username.taken": "That username is taken",
    "settings.username.saved": "Saved ✓",
    "settings.admin.desc": "View users, balance, runtime state, and backend errors.",
    "settings.admin.open": "Open admin",
    "cron.title": "Scheduled tasks",
    "cron.empty": "No scheduled tasks yet. Create one below — the agent runs it on schedule and the result lands in your history.",
    "cron.createTitle": "New scheduled task",
    "cron.name": "Name",
    "cron.prompt": "Prompt (sent to your agent on schedule)",
    "cron.schedule": "Schedule",
    "cron.kindDaily": "Daily at",
    "cron.kindInterval": "Every (minutes)",
    "cron.every": "every",
    "cron.minutes": "min",
    "cron.dailyAt": "daily at",
    "cron.next": "next",
    "cron.paused": "paused",
    "cron.pause": "Pause",
    "cron.resume": "Resume",
    "cron.delete": "Delete",
    "cron.create": "Create",
    "session.noOutput": "(no output)",
    "agent.attachmentLabel": "attachment",

    "status.provisioning": "provisioning",
    "status.ready": "ready",
    "status.failed": "failed",
    "status.suspended": "suspended",
    "status.queued": "queued",
    "status.running": "running",
    "status.completed": "completed",
    "status.cancelled": "cancelled",

    "common.failed": "Failed"
  }
};

export type TFunc = (key: string, vars?: Vars) => string;

interface I18nState {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: TFunc;
}

const I18nContext = createContext<I18nState | null>(null);

// Pure translation lookup — exported so the fallback + interpolation behavior is
// characterization-tested without rendering React (see i18n.test.ts). The provider's
// t() delegates to this so there is exactly one source of truth for the behavior.
export function translate(lang: Lang, key: string, vars?: Vars): string {
  let s = translations[lang][key] ?? translations.en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
  }
  return s;
}

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "zh" || saved === "en") return saved;
  } catch {
    /* ignore */
  }
  return "zh"; // Chinese by default
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);

  const setLang = useCallback((next: Lang) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = next === "zh" ? "zh-CN" : "en";
    setLangState(next);
  }, []);

  const t = useCallback<TFunc>(
    (key, vars) => translate(lang, key, vars),
    [lang]
  );

  // Keep the document title + <html lang> in sync with the current language.
  useEffect(() => {
    document.title = t("brand");
    document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";
  }, [lang, t]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nState {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

/** Small EN / 中文 toggle. */
export function LangToggle({ className }: { className?: string }) {
  const { lang, setLang, t } = useI18n();
  return (
    <div className={`lang-toggle ${className ?? ""}`} role="group" aria-label="Language">
      <button
        type="button"
        className={lang === "zh" ? "active" : ""}
        onClick={() => setLang("zh")}
        aria-pressed={lang === "zh"}
      >
        {t("lang.zh")}
      </button>
      <button
        type="button"
        className={lang === "en" ? "active" : ""}
        onClick={() => setLang("en")}
        aria-pressed={lang === "en"}
      >
        {t("lang.en")}
      </button>
    </div>
  );
}
